/**
 * Real feature detection for getUserMedia support, used instead of the
 * Acuant SDK's own `AcuantCamera.isCameraSupported`. That flag is
 * `"mediaDevices" in navigator && <User-Agent mobile regex>` — on a desktop
 * browser (even one with a real webcam) the regex never matches "mobile",
 * so it always reports false and the app falls back to manual/file-based
 * capture instead of ever trying the live camera.
 */
export const hasRealCameraSupport = () =>
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

// Device labels commonly used by virtual-camera software that can inject a
// pre-recorded/AI-generated video or image as if it were a live webcam feed.
// This is a heuristic, not a guarantee: labels can be renamed. It raises the
// bar against off-the-shelf spoofing tools without adding friction for real
// phone cameras (which never match these labels).
const SUSPICIOUS_CAMERA_KEYWORDS = [
    "obs",
    "virtual",
    "manycam",
    "splitcam",
    "xsplit",
    "snap camera",
    "e2esoft",
    "vcam",
    "iriun",
    "epoccam",
    "droidcam",
    "camtwist",
    "ndi",
    "ecamm",
    "chromacam",
    "youcam",
    "camo",
    "streamlabs",
    "wondershare",
    "fake",
    "loopback",
];

const isSuspiciousDeviceLabel = (label) => {
    const normalized = (label || "").toLowerCase();
    return SUSPICIOUS_CAMERA_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

/**
 * Requests camera access (so device labels are populated), then checks the
 * available video input devices against a blocklist of known virtual-camera
 * software. Fails open (returns true) on any error so it never blocks a
 * legitimate user due to a permission prompt, timing issue, or unsupported
 * API — this is an additional signal, not the primary security control.
 */
export const verifyRealCameraDevice = async () => {
    if (!navigator?.mediaDevices?.enumerateDevices || !navigator?.mediaDevices?.getUserMedia) {
        return { isReal: true };
    }

    let tempStream;
    try {
        tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((device) => device.kind === "videoinput");
        const suspiciousDevice = videoInputs.find((device) => isSuspiciousDeviceLabel(device.label));

        return {
            isReal: !suspiciousDevice,
            suspiciousLabel: suspiciousDevice?.label,
            videoInputLabels: videoInputs.map((device) => device.label),
        };
    } catch (error) {
        return { isReal: true, error };
    } finally {
        tempStream?.getTracks()?.forEach((track) => track.stop());
    }
};
